import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { formatCurrency, toBaseCurrency, formatDate, API_URL } from "../lib/utils";
import axios from "axios";
import { toast } from "sonner";
import { Gift, Plus, Search, PlusCircle, MinusCircle, Ban, History } from "lucide-react";

const STATUS = {
  active: { label: "Ativo", cls: "bg-emerald-500/20 text-emerald-600" },
  used: { label: "Usado", cls: "bg-slate-500/20 text-slate-500" },
  expired: { label: "Expirado", cls: "bg-red-500/20 text-red-500" },
  cancelled: { label: "Cancelado", cls: "bg-red-500/20 text-red-500" },
};

export default function GiftCardsPage() {
  const { token } = useAuth();
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const [cards, setCards] = useState([]);
  const [modal, setModal] = useState(false);
  const [opModal, setOpModal] = useState(null); // {card, type: 'topup'|'redeem'}
  const [movementsModal, setMovementsModal] = useState(false);
  const [movements, setMovements] = useState([]);
  const [amount, setAmount] = useState("");
  const [search, setSearch] = useState("");
  const [form, setForm] = useState({ type: "gift", initial_balance: "", customer_name: "", expiry_date: "" });

  useEffect(() => { fetchAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAll = async () => {
    try {
      const res = await axios.get(`${API_URL}/gift-cards`, { headers, withCredentials: true });
      setCards(res.data);
    } catch { toast.error("Erro ao carregar cartões"); }
  };

  const create = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API_URL}/gift-cards`, {
        ...form,
        initial_balance: toBaseCurrency(form.initial_balance),
        expiry_date: form.expiry_date || null,
      }, { headers, withCredentials: true });
      toast.success(`Cartão criado: ${res.data.code}`);
      setModal(false); setForm({ type: "gift", initial_balance: "", customer_name: "", expiry_date: "" });
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };

  const doOperation = async (e) => {
    e.preventDefault();
    const { card, type } = opModal;
    try {
      const res = await axios.post(`${API_URL}/gift-cards/${card.card_id}/${type}?amount=${toBaseCurrency(amount)}`, {}, { headers, withCredentials: true });
      toast.success(`${res.data.message} — saldo: ${formatCurrency(res.data.balance)}`);
      setOpModal(null); setAmount("");
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };

  const cancel = async (card) => {
    if (!window.confirm(`Cancelar o cartão ${card.code}?`)) return;
    await axios.put(`${API_URL}/gift-cards/${card.card_id}/cancel`, {}, { headers, withCredentials: true });
    fetchAll();
  };

  const openMovements = async (card) => {
    try {
      const res = await axios.get(`${API_URL}/gift-cards/${card.card_id}/movements`, { headers, withCredentials: true });
      setMovements(res.data);
      setMovementsModal(true);
    } catch { toast.error("Erro ao carregar movimentos"); }
  };

  const filtered = cards.filter((c) =>
    !search || c.code.toLowerCase().includes(search.toLowerCase()) || (c.customer_name || "").toLowerCase().includes(search.toLowerCase())
  );

  const totalBalance = cards.filter((c) => c.status === "active").reduce((s, c) => s + (c.balance || 0), 0);

  return (
    <div className="space-y-6" data-testid="giftcards-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold font-['Outfit']">Gift Cards & Cartões de Consumo</h1>
          <p className="text-muted-foreground">Crie, carregue e debite cartões pré-pagos dos seus clientes</p>
        </div>
        <Button onClick={() => setModal(true)} data-testid="add-giftcard-btn"><Plus className="w-4 h-4 mr-2" /> Novo Cartão</Button>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Cartões Ativos</p><p className="text-2xl font-bold">{cards.filter((c) => c.status === "active").length}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Saldo Total em Cartões</p><p className="text-2xl font-bold font-mono">{formatCurrency(totalBalance)}</p></CardContent></Card>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input className="pl-10" placeholder="Pesquisar por código ou cliente" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="giftcard-search-input" />
      </div>

      {filtered.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground"><Gift className="w-10 h-10 mx-auto mb-3 opacity-40" />Nenhum cartão encontrado</CardContent></Card>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {filtered.map((c) => {
            const st = STATUS[c.status] || STATUS.active;
            return (
              <Card key={c.card_id} data-testid={`giftcard-${c.card_id}`}>
                <CardContent className="p-4 space-y-2">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-mono font-bold text-lg">{c.code}</p>
                      <p className="text-xs text-muted-foreground">
                        {c.type === "gift" ? "Gift Card" : "Cartão de Consumo"}
                        {c.customer_name ? ` · ${c.customer_name}` : ""}
                        {c.expiry_date ? ` · válido até ${c.expiry_date}` : ""}
                      </p>
                    </div>
                    <Badge className={st.cls}>{st.label}</Badge>
                  </div>
                  <p className="text-2xl font-black font-mono" data-testid={`giftcard-balance-${c.card_id}`}>{formatCurrency(c.balance)}</p>
                  <div className="flex gap-1.5 flex-wrap pt-1">
                    {c.status === "active" && (
                      <>
                        <Button size="sm" variant="outline" onClick={() => { setOpModal({ card: c, type: "topup" }); setAmount(""); }} data-testid={`topup-${c.card_id}`}>
                          <PlusCircle className="w-4 h-4 mr-1" /> Carregar
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => { setOpModal({ card: c, type: "redeem" }); setAmount(""); }} data-testid={`redeem-${c.card_id}`}>
                          <MinusCircle className="w-4 h-4 mr-1" /> Consumir
                        </Button>
                      </>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => openMovements(c)}><History className="w-4 h-4" /></Button>
                    {c.status === "active" && (
                      <Button size="sm" variant="ghost" className="text-destructive" onClick={() => cancel(c)}><Ban className="w-4 h-4" /></Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={modal} onOpenChange={setModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Novo Cartão</DialogTitle></DialogHeader>
          <form onSubmit={create} className="space-y-3">
            <div className="space-y-1">
              <Label>Tipo</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="giftcard-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="gift">Gift Card (oferta)</SelectItem>
                  <SelectItem value="consumo">Cartão de Consumo (recarregável)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1"><Label>Saldo Inicial *</Label><Input required type="number" step="0.01" min="0.01" value={form.initial_balance} onChange={(e) => setForm({ ...form, initial_balance: e.target.value })} data-testid="giftcard-balance-input" /></div>
            <div className="space-y-1"><Label>Cliente (opcional)</Label><Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} /></div>
            <div className="space-y-1"><Label>Validade (opcional)</Label><Input type="date" value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setModal(false)}>Cancelar</Button>
              <Button type="submit" data-testid="save-giftcard-btn">Criar Cartão</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!opModal} onOpenChange={() => setOpModal(null)}>
        <DialogContent className="max-w-xs">
          <DialogHeader>
            <DialogTitle>{opModal?.type === "topup" ? "Carregar" : "Consumir"} — {opModal?.card?.code}</DialogTitle>
          </DialogHeader>
          <form onSubmit={doOperation} className="space-y-3">
            <p className="text-sm text-muted-foreground">Saldo atual: <strong className="font-mono">{formatCurrency(opModal?.card?.balance)}</strong></p>
            <Input required type="number" step="0.01" min="0.01" placeholder="Valor" value={amount} onChange={(e) => setAmount(e.target.value)} autoFocus data-testid="operation-amount-input" />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpModal(null)}>Cancelar</Button>
              <Button type="submit" data-testid="confirm-operation-btn">{opModal?.type === "topup" ? "Carregar" : "Consumir"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={movementsModal} onOpenChange={setMovementsModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Movimentos do Cartão</DialogTitle></DialogHeader>
          <div className="max-h-64 overflow-y-auto space-y-1">
            {movements.map((m) => (
              <div key={m.movement_id} className="flex justify-between items-center p-2 rounded border text-sm">
                <Badge className={m.type === "topup" ? "bg-emerald-500/20 text-emerald-600" : "bg-amber-500/20 text-amber-600"}>
                  {m.type === "topup" ? "Carregamento" : "Consumo"}
                </Badge>
                <span className="font-mono font-bold">{formatCurrency(m.amount)}</span>
                <span className="text-xs text-muted-foreground">{formatDate(m.created_at)}</span>
              </div>
            ))}
            {movements.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">Sem movimentos</p>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
